// @catalog F.010
/**
 * SalesPopover — quick view ventas últimos 4 meses (Fase 7.b).
 *
 * Spec literal:
 *   - README §5 Popovers (líneas 169-178).
 *   - prototype/forecast-page.jsx 669-691 (render sales).
 *   - AGENT_PROMPT.md línea 85 — 4 filas con qty + amount + total.
 *
 * Comportamiento:
 *   - Misma estructura que InventoryPopover (mismo PopoverShell).
 *   - Header "Ventas últimos 4 meses · {SKU}".
 *   - 4 filas con mes formateado (es-MX: "mar 2026") + qty pz + amount MXN compact.
 *   - Footer "Total {qty}pz" + amount total MXN compact.
 *   - Link "Ver detalle completo →" abre SalesDetailsDialog modal existente.
 *   - Si product.salesByLastMonths ausente: muestra empty hint + link al modal.
 *   - Click outside o ESC cierra (PopoverShell maneja).
 */
import React from "react";
import { Box, Stack } from "@mui/material";
import { PopoverShell, type AnchorRect } from "./popover-shell";
import type { ProductData } from "./types";

export interface SalesPopoverProps {
  open: boolean;
  anchorRect: AnchorRect | null;
  product: ProductData | null;
  onClose: () => void;
  onOpenFullDialog: () => void;
}

const mxnCompact = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  notation: "compact",
  maximumFractionDigits: 2,
});

const MONTH_NAMES_ES = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

export function formatMonthKeyEs(key: string): string {
  // key esperado YYYY-MM (ej. "2026-03")
  const parts = key.split("-");
  const y = parts[0] ?? "";
  const m = parts[1] ?? "";
  const idx = parseInt(m, 10) - 1;
  const name = MONTH_NAMES_ES[idx] ?? m;
  const yy = y.length === 4 ? y.slice(2) : y;
  return `${name} ${yy}`;
}

export function SalesPopover(props: SalesPopoverProps): React.ReactElement | null {
  const { open, anchorRect, product, onClose, onOpenFullDialog } = props;
  if (!product) return null;

  const breakdown = (product.salesByLastMonths ?? []).slice(-4);
  const titleSuffix = product.unifiedCode ? ` · ${product.unifiedCode}` : "";
  const totalQty = breakdown.reduce((s, r) => s + r.qty, 0);
  const totalAmt = breakdown.reduce((s, r) => s + r.amount, 0);

  return (
    <PopoverShell
      open={open}
      anchorRect={anchorRect}
      onClose={onClose}
      onOpenFullDialog={onOpenFullDialog}
      title={`Ventas últimos 4 meses${titleSuffix}`}
      testId="forecast-sales-popover"
      footer={
        <>
          <Box component="span" sx={{ color: "var(--ink-2)" }}>
            Total {totalQty.toLocaleString("es-MX")} pz
          </Box>
          <Box component="strong" sx={{ color: "var(--ink-1)", fontWeight: 600 }}>
            {mxnCompact.format(totalAmt)}
          </Box>
        </>
      }
    >
      {breakdown.length === 0 ? (
        <Box
          data-testid="forecast-sales-popover-empty"
          sx={{ color: "var(--ink-3)", fontSize: 12, py: "8px" }}
        >
          Sin desglose mensual disponible. Abrí el detalle completo para ver el historial.
        </Box>
      ) : (
        breakdown.map((row, i) => (
          <Stack
            key={`${row.monthKey}-${i}`}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            data-testid={`forecast-sales-popover-row-${i}`}
            sx={{
              padding: "4px 0",
              borderBottom: "1px solid var(--border)",
              fontVariantNumeric: "tabular-nums",
              "&:last-child": { borderBottom: 0 },
            }}
          >
            <Box component="span" sx={{ color: "var(--ink-2)" }}>
              {formatMonthKeyEs(row.monthKey)}
            </Box>
            <Stack direction="row" gap="12px" sx={{ fontVariantNumeric: "tabular-nums" }}>
              <Box
                component="span"
                sx={{ color: "var(--ink-3)", fontWeight: 400 }}
              >
                {row.qty.toLocaleString("es-MX")} pz
              </Box>
              <Box
                component="span"
                sx={{ fontWeight: 500, color: "var(--ink-1)" }}
              >
                {mxnCompact.format(row.amount)}
              </Box>
            </Stack>
          </Stack>
        ))
      )}
    </PopoverShell>
  );
}
